// client/src/features/repairs/search.ts — SCR-REP-001's URL state · MAINT-007 (04 §9.2.3).
//
// §9.2.3 is normative for every LIST screen: filters, pagination and sort live in the URL as
// Zod-validated search params, so a filtered board is shareable, survives refresh, and Back is
// a filter-undo. `features/contracts/search.ts` is the shape this copies.
//
// The names are `GET /api/v1/repair-tickets`'s own — `status`, `propertyId`, `tenantId`,
// `reportedVia`, `openOnly`, `overdueOnly`, `minAgeDays`, `q` plus the 02 §7.4 paging — so the URL of the
// screen and the query string of the request are the same vocabulary and `toListQuery` is a
// rename-free pass-through.
//
// `view` is the ONE param that is not the endpoint's. 05 MAINT-007 asks for „ticket list/board
// by status" — two renderings of one set — and which one a user is looking at is exactly the
// kind of state §9.2.3 wants in the URL: a colleague sent the board, not the table.

import { z } from 'zod';

import { REPAIR_TICKET_STATUSES, REPORTED_VIA_CHANNELS } from './enums';

/** The two renderings of SCR-REP-001. The board is the default: an incident is a thing to move. */
export const REPAIR_VIEWS = ['board', 'list'] as const;

export type RepairView = (typeof REPAIR_VIEWS)[number];

/** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
export const repairSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  sort: z.string().optional(),
  q: z.string().trim().optional(),
  status: z.enum(REPAIR_TICKET_STATUSES).optional(),
  propertyId: z.string().uuid().optional(),
  tenantId: z.string().uuid().optional(),
  reportedVia: z.enum(REPORTED_VIA_CHANNELS).optional(),
  /** „Doar tichete deschise" — the endpoint's own predicate over the three non-terminal states. */
  openOnly: z.coerce.boolean().optional(),
  /**
   * „Doar depășite" — the tickets still running past their fix-by, by the server's rule and the
   * server's clock (D-2473). The endpoint's predicate, not a client-side filter over one page.
   */
  overdueOnly: z.coerce.boolean().optional(),
  minAgeDays: z.coerce.number().int().min(0).optional(),
  view: z.enum(REPAIR_VIEWS).default('board'),
});

export type RepairSearch = z.infer<typeof repairSearchSchema>;

/**
 * Which filters are engaged — the empty state needs to know why it is empty. `view` is not one
 * of them: switching to the table does not filter anything out, so a board with no tickets and
 * a table with no tickets are the same emptiness and get the same „deschide primul tichet".
 */
export function hasActiveFilters(search: RepairSearch): boolean {
  return (
    (search.q !== undefined && search.q !== '') ||
    search.status !== undefined ||
    search.propertyId !== undefined ||
    search.tenantId !== undefined ||
    search.reportedVia !== undefined ||
    search.minAgeDays !== undefined ||
    search.openOnly === true ||
    search.overdueOnly === true
  );
}

/** Clearing filters returns to page 1 and keeps the rendering the user chose. */
export function clearedSearch(view: RepairView): RepairSearch {
  return { page: 1, pageSize: 25, view };
}
